import EmberService, { service } from '@ember/service';

import type { MetricInfoCardEntryPointProps } from '@databox/ember-integrations/lib/components/MetricInfoCard/MetricInfoCardEntryPoint';
import type { MetricsEntryPointProps } from '@databox/ember-integrations/lib/components/Metrics/MetricsEntryPoint';
import type { MetricSelectEntryPointProps } from '@databox/ember-integrations/lib/components/MetricSelect/MetricSelectEntryPoint';
import type {
	MetricSource,
	MetricStatusObject,
	MetricUsage,
} from '@databox/types/domains/metric/metric';
import type {
	MetricRegistryItem,
	MetricRegistryTag,
	RegisteredMetric,
} from '@databox/types/domains/metric/metric-registry';
import { MetricUsageType } from '@databox/types/domains/metric/metric.enums';
import { UserActionType } from '@databox/types/domains/source/source';
import type DSModel from '@ember-data/model';

import type { APIData, DSSetup, PopupData, RequestError, Service } from 'webapp-types';
import {
	BillingFeature,
	MetricDetailsTab,
	MetricType,
	ModelName,
	PopupEvent,
	TeamSectionItemType,
} from 'webapp/enums/enums';
import { NotificationCreateType } from 'webapp/enums/notifications';
import { TrackingEvent } from 'webapp/enums/tracking';
import { compact } from 'webapp/utils/common';

import { MetricTabId } from 'webapp/components/metric-library/popups/popup-metric-extra-info';

import type { CustomMetric, Metric } from 'webapp/models-custom';
import CustomQuery from 'webapp/models-custom/custom-query';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type Popup from 'webapp/models-custom/popup/base';
import Script from 'webapp/models-custom/script';
import StatusObject from 'webapp/models-custom/status-object';
import type { StatusObjectAction } from 'webapp/models-custom/status-object';

type MetricsHost = MetricsEntryPointProps['host'];
type MetricEditResult = Awaited<ReturnType<MetricsHost['onEdit']>>;
type MetricOwnerChange = Awaited<ReturnType<MetricsHost['onPickOwner']>>;
type MetricTagsEditResult = Awaited<ReturnType<MetricsHost['onEditTags']>>;
type MetricSelectHost = MetricSelectEntryPointProps['host'];
type MetricInfoCardHost = MetricInfoCardEntryPointProps['host'];
type MetricSelectSelection = Parameters<MetricSelectHost['onSelect']>[0];

// What the Metric Select's host needs from its Ember shell.
export interface MetricSelectShell {
	close: () => void;
	reposition: () => void;
	// False once the popup closed, e.g. while a Connect was open (a route change, or dismissed).
	isOpen: () => boolean;
}

// The tracking origin the flows get for the Metrics page.
const ORIGIN = 'metrics';
// The tracking origin of the metric info card's Connect.
const METRIC_INFO_ORIGIN = 'metric_info';
// The tracking origin of the metrics the Metric Select creates.
const METRIC_SELECT_ORIGIN = 'metric popup';
// The tracking origin of what the usages popup opens.
const POPUP_USAGES_ORIGIN = 'popup_usages';

// The source fixes that go through Connect, so their promise knows whether the source was fixed.
const CONNECTION_FIXES: ReadonlySet<string> = new Set([
	UserActionType.RECONNECT,
	UserActionType.REAUTHORIZE,
	UserActionType.SOURCE_CONNECTED,
]);

// A metric created from one of these is tracked as a custom query's or a calculation's creation.
const QUERY_TYPES: ReadonlySet<MetricType> = new Set([
	MetricType.CUSTOM_QUERY,
	MetricType.CUSTOM_QUERY_GENERIC,
]);
const CALCULATION_TYPES: ReadonlySet<MetricType> = new Set([
	MetricType.METRIC_CALCULATION,
	MetricType.METRIC_CALCULATION_GENERIC,
]);

// The notification types, opened in their edit popup (they have no view); a digest is the METRIC type.
const NOTIFICATION_TYPE_BY_USAGE: Partial<Record<MetricUsageType, NotificationCreateType>> = {
	[MetricUsageType.ALERT]: NotificationCreateType.ALERT,
	[MetricUsageType.SCORECARD]: NotificationCreateType.SCORECARD,
	[MetricUsageType.DIGEST]: NotificationCreateType.METRIC,
	[MetricUsageType.SMART_ALERT]: NotificationCreateType.SMART_ALERT,
};

// The record field each editable model keeps its name in.
const getNameField = (model: CustomMetric | Metric): string => {
	if (model instanceof CustomQuery) {
		return 'queryName';
	}
	return model instanceof Script ? 'description' : 'name';
};

// Every field but the name and the save stamp: a change here is one the page can't patch in place.
const describeOtherFields = (model: CustomMetric | Metric): string => {
	const fields: Record<string, unknown> = { ...model.toJSON() };
	delete fields[getNameField(model)];
	delete fields.modified;
	return JSON.stringify(fields);
};

/**
 * The webapp's side of the React Metrics page (`MetricsEntryPoint`'s `host`): each callback runs the
 * existing flow for a registry row. A flow that can change rows resolves once it closed, cancel
 * included, with whether something was saved, so the page knows when to refresh.
 */
export default class MetricRegistryService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('connect') declare connectService: Service.Connect;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	// The page's `subscribeToChanges` listeners.
	private readonly changeListeners = new Set<() => void>();
	// The editors the page's own Edit opened: their change reaches the page through `onEdit`'s result.
	private readonly pageEditors = new WeakSet<Popup>();

	// Lineage opens in the explorer the page renders, so the page passes its opener in.
	createHost(openLineage: (metricKey: string) => void): MetricsHost {
		return {
			onView: (item) => {
				// The mobile drawer loads the metrics it shows itself, without the editing data view() waits for.
				if (this.navigationService.isMobileApp) {
					this.metricDetailsService.openMetricDetailsDrawer(
						{ metricSetup: new MetricSetup({ ...item.metricSetup }) },
						'mobile',
					);
					return;
				}

				void this.view(item);
			},
			onAnalyze: (item) => {
				void this.analyze(item);
			},
			onAddToDataboard: (items) => {
				void this.addToDataboard(items);
			},
			onAddToSpace: (items) => {
				void this.addToSpace(items);
			},
			onSetGoal: (item) => {
				void this.setGoal(item);
			},
			onOpenNotifications: (item) => {
				void this.openNotifications(item);
			},
			onOpenLineage: (item) => {
				const metricKey = new MetricSetup({ ...item.metricSetup }).lineageMetricKey;

				if (metricKey) {
					openLineage(metricKey);
				}
			},
			onViewDimensions: (item) => {
				void this.viewDimensions(item);
			},
			onOpenActivityLog: (item) => {
				this.openActivityLog(item);
			},
			onPurgeData: (item) => {
				void this.purgeData(item);
			},
			onEdit: (item) => this.edit(item, false),
			onEditDefinition: (item) => this.edit(item, true),
			onPickOwner: (item) => this.pickOwner(item),
			onEditTags: (item, anchor) => this.editTags(item, anchor),
			onPickTags: (anchor) => this.pickTags(anchor),
			onOpenMetricLibrary: () => {
				this.navigationService.transitionToRoute('metric-library');
			},
			onCreateCustomMetric: () => this.createMetric(false),
			onCreateCalculatedMetric: () => this.createMetric(true),
			onCreateFromGeneric: (item) => this.createFromGeneric(item),
			onStatusAction: (item, source) => this.runStatusAction(item, source),
			onOpenUsage: (usage) => this.openUsage(usage),
			subscribeToChanges: (listener) => {
				this.changeListeners.add(listener);
				return () => this.changeListeners.delete(listener);
			},
			runBatch: ({ operationType, objectIds, data }) =>
				this.mmMetricsService.batchMetricRegistryAction(operationType, objectIds, data),
			showToast: this.aiChatService.showToast,
			showDemoLimitation: () => {
				this.accountService.handleDemo();
			},
		};
	}

	/**
	 * The webapp's side of the React metric info card (`MetricInfoCardEntryPoint`'s `host`): the Metrics page's flows,
	 * plus the card's About, Connect and Ask a teammate. The card looks its metric up again on every click open, so it
	 * needs no change feed; its own writes reach the Metrics page's listeners. The card opens over any page, so its
	 * editors open in place, not on the Metrics page's edit route.
	 */
	createMetricInfoHost(openLineage: (metricKey: string) => void): MetricInfoCardHost {
		return {
			...this.createHost(openLineage),
			onEdit: (item) => this.edit(item, false, false),
			onStatusAction: (item, source) => this.runStatusAction(item, source, false),
			onCreateFromGeneric: (item) => this.createFromGeneric(item, METRIC_INFO_ORIGIN),
			subscribeToChanges: () => () => undefined,
			onAbout: (item) => {
				void this.about(item);
			},
			onConnect: (item) => this.connect(item),
			onAskTeammateToConnect: (item) => {
				this.askTeammateToConnect(item);
			},
			// After a write from the card settled (a delete included).
			onItemChanged: () => {
				this.notifyChange();
			},
		};
	}

	/**
	 * The webapp's side of the React Metric Select (`MetricSelectEntryPoint`'s `host`), for the popup `shell` that
	 * renders it. Every pick goes to the opener as `onChange(metricSetupData)`, then the shell closes, as the Ember popup
	 * did. A pick resolved after the shell closed (a Connect, a reload) is dropped.
	 */
	createMetricSelectHost(
		popupData: PopupData.Form.MetricSelect,
		shell: MetricSelectShell,
	): MetricSelectHost {
		const pick = (metricSetupData: DSSetup.MetricSetupData): void => {
			if (!shell.isOpen()) {
				return;
			}

			popupData.onChange(metricSetupData);
			shell.close();
		};

		// A client space's metric isn't in this space's preload: it goes out as picked.
		const isOtherSpace = !!popupData.clientSpaceId;

		const select = (selection: MetricSelectSelection): void => {
			if (isOtherSpace) {
				pick({ ...selection });
				return;
			}

			void this.findMetricSetupData(selection).then(pick);
		};

		return {
			onSelect: select,
			onConnect: (item) => this.connectForMetricSelect(item, popupData.origin, pick),
			onCreateFromGeneric: ({ metricSetup }) => {
				if (metricSetup && metricSetup.spaceAccessId !== null) {
					void this.resolvePredefined(
						metricSetup.key,
						metricSetup.spaceAccessId,
						pick,
					).then((isCreated) => {
						if (isCreated) {
							this.notifyChange();
						}
					});
				}
			},
			// Picks the saved metric like a row, as Connect and Create pick what their flow made.
			onCreateCustomMetric: async () => {
				const selection = await this.createCustomMetricForMetricSelect();

				if (selection) {
					select(selection);
				}
			},
			// A star's write settled in the popup: the Metrics page behind it reloads, as for the info card.
			onItemChanged: () => {
				this.notifyChange();
			},
			onClose: () => {
				shell.close();
			},
			onResize: () => {
				shell.reposition();
			},
			showToast: this.aiChatService.showToast,
		};
	}

	/**
	 * A custom metric editor the edit route opened (its URL, shared or reloaded). The page didn't start it, so a save,
	 * a delete or a duplicate made in it reaches the page here, unless the page's own Edit turns out to have opened it.
	 */
	watchEditor(popup: Popup, model: CustomMetric): void {
		const { modified } = model.model;
		const savedIds = this.getSavedRecordIds();

		void this.whenClosed(popup).then(() => {
			if (this.pageEditors.has(popup)) {
				return;
			}

			const saved = this.findSavedCustomMetric(model);

			if (
				!saved ||
				saved.model.isDeleted ||
				saved.model.modified !== modified ||
				this.hasCreatedRecords(savedIds)
			) {
				this.notifyChange();
			}
		});
	}

	// ------ Flows that change no row ------

	private async view(item: MetricRegistryItem): Promise<void> {
		const metricSetup = await this.findMetricSetup(item);

		if (metricSetup) {
			this.metricDetailsService.openMetricDetailsDrawer({ metricSetup }, ORIGIN);
		}
	}

	private async analyze(item: MetricRegistryItem): Promise<void> {
		const metricSetup = await this.findMetricSetup(item);

		if (metricSetup) {
			this.aiChatService.openGenieForMetricSetup(metricSetup);
		}
	}

	// One metric: its own block popup. Several: one dashboard pick, then a block each.
	private async addToDataboard(items: RegisteredMetric[]): Promise<void> {
		const [first] = items;

		if (items.length === 1 && first) {
			const metricSetup = await this.findMetricSetup(first);

			if (metricSetup) {
				this.mmBlocksService.openCreateFromMetricSetupPopup({
					metricSetup,
					trackOrigin: 'metric',
					createdNow: false,
				});
			}
			return;
		}

		await this.mmMetricsService.prepareForMetricEditing();

		const metrics = compact(
			items.map(({ metricSetup: { key, spaceAccessId } }) =>
				this.mmMetricsService.getMetricForKeyAndSpaceAccessId(key, spaceAccessId),
			),
		);

		if (metrics.length) {
			await this.mmMetricsService.addMetricsToBoard(metrics);
		}
	}

	// The space and section are picked once; every metric goes into that section.
	private async addToSpace(items: MetricRegistryItem[]): Promise<void> {
		await this.mmMetricsService.prepareForMetricEditing();

		const metricSetupData = compact(
			items.map((item) => new MetricSetup({ ...item.metricSetup }).metricSetupData),
		);

		if (metricSetupData.length) {
			this.mmTeamSectionsService.openAddPopup({
				objectType: TeamSectionItemType.METRIC,
				metricSetupData,
			});
		}
	}

	private async setGoal(item: MetricRegistryItem): Promise<void> {
		const metricSetup = await this.findMetricSetup(item);

		if (metricSetup) {
			this.mmGoalsService.openNewPopupForMetricSetup(metricSetup, ORIGIN);
		}
	}

	// As My Metrics does: the first notification type the account allows.
	private async openNotifications(item: MetricRegistryItem): Promise<void> {
		const metricSetup = await this.findMetricSetup(item);

		if (!metricSetup) {
			return;
		}

		await this.mmNotificationsService.requestNotificationsForFeature('metric');

		const option = this.mmNotificationsService.getNotificationsDropdownOptionForFeature(
			metricSetup,
			'createNotification',
		);

		if (!option) {
			this.infolayerService.info('Notifications are not available for this metric.');
			return;
		}

		this.mmNotificationsService.createAndEditForTypeAndMetricSetup(
			option.id as NotificationCreateType,
			metricSetup,
			ORIGIN,
		);
	}

	// The usages popup's View: the type's own view (edit for notifications), as a popup or drawer stacked over the
	// popup, which stays open beneath; resolves once that closed. Drawers, not the list pages' routes: a transition
	// closes every popup. A home widget and a space are the exceptions and go to their page. `popup` is `null` when
	// the usage's object isn't there (deleted, or not visible), `undefined` when the opener opened nothing.
	private async openUsage(usage: MetricUsage): Promise<void> {
		const notificationType = NOTIFICATION_TYPE_BY_USAGE[usage.type];
		let popup: Popup | null | undefined;

		if (notificationType) {
			// The plan lock the notifications list and its edit route put on a type the plan lost.
			const limitation =
				this.mmNotificationsService.checkLimitationForCreateType(notificationType);

			if (limitation) {
				this.billingService.handleLimitations(limitation);
				return;
			}

			await this.mmNotificationsService.requestNotificationsForFeature('metric');

			const model = this.mmNotificationsService.getModelForTypeAndId(
				notificationType,
				usage.id,
			);

			popup = model
				? this.mmNotificationsService.openEditPopup(model, POPUP_USAGES_ORIGIN)
				: null;
		} else {
			switch (usage.type) {
				case MetricUsageType.BOARD: {
					const board = this.mmBoardsService.findModel(usage.id);

					popup =
						board && !board.deleted
							? await this.mmBoardsService.openPreviewAndGetPopup(
									board,
									POPUP_USAGES_ORIGIN,
								)
							: null;
					break;
				}
				case MetricUsageType.REPORT: {
					await this.mmReportsService.requestReports();

					const report = this.mmReportsService.findModel(usage.id);

					popup =
						report && !report.deleted
							? await this.mmReportsService.openPreviewAndGetPopup(
									report,
									POPUP_USAGES_ORIGIN,
								)
							: null;
					break;
				}
				case MetricUsageType.TEMPLATE: {
					await Promise.all([
						this.mmTemplatesService.requestBoardAccountTemplates(),
						this.mmTemplatesService.requestBoardLoopAccountTemplates(),
						this.mmTemplatesService.requestReportAccountTemplates(),
					]);

					const template = this.mmTemplatesService.findModel(usage.id);

					popup = template
						? this.mmTemplatesService.openPreviewPopup(template, () =>
								this.mmTemplatesService.createFromAccountTemplate(template),
							)
						: null;
					break;
				}
				case MetricUsageType.GOAL: {
					await this.mmMetricsService.prepareForMetricEditing();

					const goal = this.mmGoalsService.findModel(usage.id);

					popup = goal ? this.mmGoalsService.openGoalInsightsDrawer(goal) : null;
					break;
				}
				case MetricUsageType.CALC_OPERAND: {
					await this.mmMetricsService.prepareForMetricEditing();

					const script = this.mmScriptsService.findModel(usage.id);

					// A script with no output metric has nothing to open: reported as gone, not a silent no-op.
					popup = script?.lastOutputMetric
						? this.metricDetailsService.openMetricDetailsDrawer(
								{
									metricSetup: new MetricSetup(
										script.lastOutputMetric.metricData || undefined,
									),
								},
								POPUP_USAGES_ORIGIN,
							)
						: null;
					break;
				}
				case MetricUsageType.FORECAST: {
					await Promise.all([
						this.mmForecastsService.requestForecasts(),
						this.mmMetricsService.requestMetrics(),
					]);

					const forecast = this.mmForecastsService.findModel(usage.id);

					popup = forecast
						? this.metricDetailsService.openMetricDetailsDrawer(
								{
									metricSetup: forecast.metricSetup,
									tab: MetricDetailsTab.FORECAST,
									forecast: { forecastId: forecast.id },
								},
								POPUP_USAGES_ORIGIN,
							)
						: null;
					break;
				}
				// Home is the only place a widget can be removed, so this goes there; the transition closes the popup.
				case MetricUsageType.HOME_WIDGET:
					this.navigationService.transitionToRoute(
						this.navigationService.isMobileApp ? 'mobile.home' : 'home',
					);
					return;
				// A space is a page, not a drawer, so this goes there too.
				case MetricUsageType.TEAM:
					this.navigationService.transitionToRoute('teams.view', usage.id);
					return;
			}
		}

		if (popup === null) {
			this.infolayerService.error('This item is no longer available.');
			return;
		}

		await this.whenClosed(popup);
	}

	private async viewDimensions(item: MetricRegistryItem): Promise<void> {
		const metric = (await this.findMetricSetup(item))?.metric;

		if (metric) {
			this.mmMetricsService.openExtraInfoPopup(metric, MetricTabId.DIMENSIONS);
		}
	}

	private openActivityLog(item: MetricRegistryItem): void {
		this.mmActivityLogsService.createAndOpenPopup({ metricRegistryItem: item });
	}

	// Only the metric's stored data goes, so the page doesn't wait for it. A horizon metric purges
	// through its own endpoint; every other type by its metric setup.
	private async purgeData(item: MetricRegistryItem): Promise<void> {
		if (item.objectType !== MetricType.HORIZON) {
			const metric = (await this.findMetricSetup(item))?.metric;

			if (metric) {
				this.mmMetricsService.purgeMetricData(metric);
			}
			return;
		}

		await this.mmMetricsService.prepareForMetricEditing();

		const horizonMetric =
			item.objectId === null
				? null
				: this.mmHorizonMetricsService.findModel(String(item.objectId));

		if (horizonMetric) {
			this.mmHorizonMetricsService.purgeData(horizonMetric);
		} else {
			this.showNotAvailable();
		}
	}

	// ------ Flows that can change rows ------

	// `isDefinition` opens the custom query or calculation editor on its metric definition. `isRouted` opens the editor
	// on the Metrics page's edit route; otherwise it opens in place, over the current page.
	private async edit(
		item: MetricRegistryItem,
		isDefinition: boolean,
		isRouted = true,
	): Promise<MetricEditResult> {
		if (item.objectType === MetricType.BASIC || item.objectType === MetricType.CUSTOM_PUSH) {
			const metric = (await this.findMetricSetup(item))?.metric;

			return metric ? this.editMetricDefinition(item, metric) : null;
		}

		await this.mmMetricsService.prepareForMetricEditing();

		return item.objectType === MetricType.GOAL
			? this.editGoal(item)
			: this.editCustomMetric(item, isDefinition, isRouted);
	}

	// Saved = the record's `modified` moved. The custom query editor's afterSave also runs on Cancel, so it can't tell.
	private async editCustomMetric(
		item: MetricRegistryItem,
		isDefinition: boolean,
		isRouted: boolean,
	): Promise<MetricEditResult> {
		const model = this.findCustomMetric(item);

		if (!model) {
			this.showNotAvailable();
			return null;
		}

		const { modified } = model.model;
		const otherFields = describeOtherFields(model);
		const savedIds = this.getSavedRecordIds();

		await this.whenClosed(await this.openCustomMetricEditor(model, isDefinition, isRouted));

		// The editor's own Duplicate saves a new metric next to this one.
		const hasCreated = this.hasCreatedRecords(savedIds);
		const saved = this.findCustomMetric(item);

		if (!saved || saved.model.isDeleted) {
			return { changes: {}, hasOtherChanges: true };
		}

		if (saved.model.modified === modified) {
			return hasCreated ? { changes: {}, hasOtherChanges: true } : null;
		}

		return this.toEditResult(
			item,
			saved.metricName,
			hasCreated || describeOtherFields(saved) !== otherFields,
		);
	}

	private async openCustomMetricEditor(
		model: CustomMetric,
		isDefinition: boolean,
		isRouted: boolean,
	): Promise<Popup | undefined> {
		if (!isDefinition && isRouted) {
			return this.openCustomMetricEditorRoute(model);
		}

		if (model instanceof CustomQuery) {
			return this.mmCustomQueriesService.openEditPopup(model, ORIGIN, isDefinition);
		}

		if (model instanceof Script) {
			return this.mmScriptsService.openEditPopup(model, ORIGIN, undefined, isDefinition);
		}

		// The horizon editor has no separate definition popup.
		return this.mmHorizonMetricsService.openEditPopup(model, ORIGIN);
	}

	// The editor opens over the Metrics page at /metrics/edit/<type>/:id, so the URL can be shared and reloaded.
	// When the route turns it away (demo, missing record, billing), it redirects back and opens nothing.
	private async openCustomMetricEditorRoute(model: CustomMetric): Promise<Popup | undefined> {
		const openBefore = [...this.popupService.popupStack];

		let transition;
		if (model instanceof CustomQuery) {
			transition = this.mmCustomQueriesService.openEditPopupWithTransition(model);
		} else if (model instanceof Script) {
			transition = this.mmScriptsService.openEditPopupWithTransition(model);
		} else {
			transition = this.mmHorizonMetricsService.openEditPopupWithTransition(model);
		}

		await transition.followRedirects().catch(() => undefined);

		const editor = this.popupService.popupStack.find(
			(popup) => !openBefore.includes(popup) && (popup.isOpen || popup.isOpening),
		);

		if (editor) {
			this.pageEditors.add(editor);
		}

		return editor;
	}

	// Basic and custom push metrics: the definition popup, whose afterSave runs only on a save.
	private async editMetricDefinition(
		item: MetricRegistryItem,
		metric: Metric,
	): Promise<MetricEditResult> {
		const otherFields = describeOtherFields(metric);

		const isSaved = await this.isSavedOnClose((onSaved) =>
			this.mmMetricsService.openMetricDefinitionPopup(metric, false, false, false, onSaved),
		);

		return isSaved
			? this.toEditResult(item, metric.name, describeOtherFields(metric) !== otherFields)
			: null;
	}

	private async editGoal(item: MetricRegistryItem): Promise<MetricEditResult> {
		const goal =
			item.objectId === null ? null : this.mmGoalsService.findModel(String(item.objectId));

		if (!goal) {
			this.showNotAvailable();
			return null;
		}

		const isSaved = await this.isSavedOnClose((onSaved) =>
			this.mmGoalsService.openEditPopup(goal, ORIGIN, onSaved),
		);

		return isSaved ? { changes: {}, hasOtherChanges: true } : null;
	}

	// The user picker; the page saves the pick. A row with no owner preselects no one. Any activated
	// user can own a metric, unverified ones and viewers included: the BE only asks for space membership.
	private async pickOwner(item: MetricRegistryItem): Promise<MetricOwnerChange> {
		let change: MetricOwnerChange = null;

		const popup = this.mmUsersService.openUserSelectPopup({
			title: 'Metric owner',
			text: 'Select an owner for a metric:',
			currentUserId: item.owner ? String(item.owner.id) : '',
			userOptions: this.mmUsersService.activatedUsersList,
			onSelect: (userId) => {
				change = { ownerId: userId ? Number(userId) : null };
			},
		});

		await this.whenClosed(popup);

		return change;
	}

	// The tags context, anchored to the page's button. It applies again once a tag is created in its tags modal, so the
	// saves run in order and the last one's tags win; resolves when it closes, with null when nothing was saved or failed.
	// The page says when the metric was deleted meanwhile (404) or a save failed; a 422 shows its own errors here.
	// A tag renamed, recoloured or deleted in the tags modal reloads the page (`notifyIfTagsChanged`).
	private async editTags(
		item: MetricRegistryItem,
		anchor: HTMLElement,
	): Promise<MetricTagsEditResult> {
		let tags: MetricRegistryTag[] | null = null;
		let isGone = false;
		let isFailed = false;
		let saves: Promise<void> = Promise.resolve();

		const saveTags = (tagIds: number[]): Promise<void> =>
			this.requestService
				.api<Pick<MetricRegistryItem, 'tags'>>(
					'PUT',
					`/metric-registry/${item.id}/tags`,
					{ tagIds },
					{ skipShowingErrorInfolayer: true },
				)
				.then((row) => {
					tags = row.tags;
					isFailed = false;
				})
				.catch((error: RequestError) => {
					if (error?.isHandled) {
						return;
					}

					if (error?.code === 404) {
						isGone = true;
					} else if (error?.code === 422) {
						this.requestService.showAllValidationErrors(error);
					} else {
						isFailed = true;
					}
				});

		const tagLooks = this.getTagLooks();
		const popup = this.mmTagsService.openTagsContext(anchor, {
			model: { tags: item.tags.map(({ id }) => id) },
			onApplySelected: (tagIds) => {
				saves = saves.then(() => saveTags(tagIds));
			},
		});

		await this.whenTagsContextClosed(popup, anchor);
		await saves;
		this.notifyIfTagsChanged(tagLooks);

		return tags || isGone || isFailed ? { tags, isGone, isFailed } : null;
	}

	// The tags context for the bulk bar, opened with none picked as on the other tables; the page adds
	// the pick to the selection. It applies again once a tag is created in its tags modal, so the last
	// pick applied is the one; resolves when it closes, with null when nothing was applied.
	private async pickTags(anchor: HTMLElement): Promise<number[] | null> {
		let tagIds: number[] | null = null;

		const tagLooks = this.getTagLooks();
		const popup = this.mmTagsService.openTagsContext(anchor, {
			model: { tags: [] },
			onApplySelected: (selected) => {
				tagIds = selected;
			},
		});

		await this.whenTagsContextClosed(popup, anchor);
		this.notifyIfTagsChanged(tagLooks);

		return tagIds;
	}

	// The table scrolls inside the page's shadow root, which the popup's own window listener never hears.
	private async whenTagsContextClosed(
		popup: Popup | null | undefined,
		anchor: HTMLElement,
	): Promise<void> {
		const scrollRoot = anchor.getRootNode();
		const reposition = () => popup?.reposition();
		scrollRoot.addEventListener('scroll', reposition, { capture: true });

		await this.whenClosed(popup);
		scrollRoot.removeEventListener('scroll', reposition, { capture: true });
	}

	// New metric's builds: the custom metric builder or the calculation editor, on a new metric.
	// True once it is saved.
	private async createMetric(isCalculated: boolean): Promise<boolean> {
		await this.mmMetricsService.prepareForMetricEditing();

		const savedIds = this.getSavedRecordIds();

		await this.whenClosed(this.openCreateMetric(isCalculated));

		return this.hasCreatedRecords(savedIds);
	}

	// The plan check each half of the custom metric popup makes; the opener checks the custom metrics limit.
	private openCreateMetric(isCalculated: boolean): Popup | undefined {
		if (isCalculated) {
			if (!this.billingService.scriptsEnabled) {
				this.billingService.handleLimitations(BillingFeature.SCRIPTS);
				return undefined;
			}

			return this.mmScriptsService.openCreateNewPopup(ORIGIN);
		}

		if (!this.billingService.qbEnabled) {
			this.billingService.handleLimitations(BillingFeature.QB);
			return undefined;
		}

		return this.mmCustomQueriesService.openCreateNewPopup(ORIGIN);
	}

	// resolveGenericMetricWithSpecific never rejects: on an error it shows it and resolves with the
	// predefined metric itself, so a new key means the space's own metric was created.
	private async createFromGeneric(
		item: RegisteredMetric,
		origin: string = ORIGIN,
	): Promise<boolean> {
		await this.mmMetricsService.requestMetrics();

		const { key, spaceAccessId } = item.metricSetup;
		const layer = this.infolayerService.loading('Creating metric...');
		const created = await this.mmMetricsService.resolveGenericMetricWithSpecific(
			key,
			spaceAccessId,
		);
		const isCreated = created.key !== key;

		if (isCreated) {
			layer.ok('Metric created!');
			this.trackCreateFromGeneric(item, origin);
		} else {
			layer.close();
		}

		return isCreated;
	}

	private trackCreateFromGeneric(item: RegisteredMetric, origin: string): void {
		const sourceTypeKey = item.sources[0]?.dataSourceTypeKey;

		if (QUERY_TYPES.has(item.objectType)) {
			this.trackMetricCreated(TrackingEvent.QUERY_CREATED, origin, sourceTypeKey);
		} else if (CALCULATION_TYPES.has(item.objectType)) {
			this.trackMetricCreated(TrackingEvent.CALCULATION_CREATED, origin, sourceTypeKey);
		}
	}

	// A source's status (`source` set) or the row's own. Connection fixes go through Connect and report
	// whether the source was fixed; the rest run as StatusObject runs them and report nothing to reload.
	private async runStatusAction(
		item: MetricRegistryItem,
		source: MetricSource | null,
		isRouted = true,
	): Promise<boolean> {
		const status = source ? source.statusObject : item.statusObject;
		const userAction = status?.userAction;

		if (!status || !userAction) {
			return false;
		}

		await this.mmMetricsService.prepareForMetricEditing();

		if (!source && userAction === UserActionType.EDIT && this.findCustomMetric(item)) {
			return (await this.edit(item, false, isRouted)) !== null;
		}

		if (source && CONNECTION_FIXES.has(userAction)) {
			return this.fixConnection(source, userAction);
		}

		this.triggerStatusAction(item, status);
		return false;
	}

	private async fixConnection(
		source: MetricSource,
		userAction: UserActionType,
	): Promise<boolean> {
		const sourceModel = this.mmSourcesService.findModel(String(source.spaceAccessId));

		// A connection the user can't fix (a private one, or not theirs): the Data manager says why.
		if (!sourceModel?.canResolveActionableStatus) {
			this.navigationService.transitionToRoute('datamanager.connected');
			return false;
		}

		const options =
			userAction === UserActionType.SOURCE_CONNECTED
				? {
						sourceId: sourceModel.numericId,
						connectionId: sourceModel.connection?.id?.toString(),
						sourceSelected: true,
					}
				: {
						sourceId: sourceModel.numericId,
						reauthorize: userAction === UserActionType.REAUTHORIZE,
					};

		const { isSuccess } = await this.connectService.connectSourceFromIdOrKey(
			source.dataSourceTypeKey,
			{ ...options, origin: ORIGIN },
		);

		return isSuccess;
	}

	// Refetch resyncs a custom query's data; a dataset's fix (edit, finish setup) goes to the Data manager.
	private triggerStatusAction(item: MetricRegistryItem, status: MetricStatusObject) {
		// @databox/types has its own enum for the same userAction string values
		const statusObject = StatusObject.createFromData({
			...status,
			userAction: status.userAction as string | null | undefined as
				StatusObjectAction | null | undefined,
		});

		if (item.objectType === MetricType.CUSTOM_QUERY) {
			statusObject.queryId = item.objectId;
		}

		statusObject.triggerAction();
	}

	// ------ Metric info card ------

	private async about(item: MetricRegistryItem): Promise<void> {
		const metric = (await this.findMetricSetup(item))?.metric;

		if (metric) {
			this.mmMetricsService.openExtraInfoPopup(metric);
		}
	}

	// `true` once a connection of the metric's integration was added.
	private async connect(item: MetricRegistryItem): Promise<boolean> {
		const sourceTypeKey = item.sources[0]?.dataSourceTypeKey;

		if (!sourceTypeKey) {
			this.showNotAvailable();
			return false;
		}

		const { isSuccess } = await this.connectService.connectSourceFromIdOrKey(sourceTypeKey, {
			origin: METRIC_INFO_ORIGIN,
		});

		return isSuccess;
	}

	// The request goes through requestConnect, which checks the email verification, the connect rights and the plan.
	private askTeammateToConnect(item: MetricRegistryItem): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const sourceTypeKey = item.sources[0]?.dataSourceTypeKey;
		const sourceType = sourceTypeKey
			? this.mmSourceTypesService.findModelByKey(sourceTypeKey)
			: null;

		if (sourceType) {
			sourceType.requestConnect();
		} else {
			this.showNotAvailable();
		}
	}

	// ------ Metric Select ------

	// The preload's metric for the pick, for its autoselected dimension; a goal by its key (goals are keyed `id === key`).
	// A metric the preload doesn't have yet (made since it loaded) gets the preload again; failing that, the pick goes
	// out as it is.
	private async findMetricSetupData(
		selection: MetricSelectSelection,
	): Promise<DSSetup.MetricSetupData> {
		await this.mmMetricsService.requestMetrics();

		const find = (): Metric | null =>
			this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
				selection.key,
				selection.spaceAccessId,
			) ?? this.findGoalMetric(selection.key);

		let metric = find();

		if (!metric) {
			await this.requestService.storeFindAll(ModelName.METRIC);
			metric = find();
		}

		return metric?.metricSetupData ?? { ...selection };
	}

	private findGoalMetric(key: string): Metric | null {
		const metric = this.mmMetricsService.findModel(key);

		return metric?.isTypeGoal ? metric : null;
	}

	// A catalogue metric or a template of an unconnected integration: connect it, then pick the row on the new
	// connection (a template is created from first). `true` once a connection was added, which adds rows to the
	// Metrics page whether or not a metric was created.
	private async connectForMetricSelect(
		item: MetricRegistryItem,
		origin: PopupData.Form.MetricSelect['origin'],
		pick: (metricSetupData: DSSetup.MetricSetupData) => void,
	): Promise<boolean> {
		// The popup offers no Connect on a row without a metric setup.
		const key = item.metricSetup?.key;
		const sourceTypeKey = item.sources[0]?.dataSourceTypeKey;
		const sourceType = sourceTypeKey
			? this.mmSourceTypesService.findModelByKey(sourceTypeKey)
			: null;

		if (!key || !sourceType) {
			this.showNotAvailable();
			return false;
		}

		const result = await sourceType.connectNew({ origin });

		if (!result.isSuccess) {
			return false;
		}

		await this.resolvePredefined(key, result.source.id, pick);
		this.notifyChange();
		return true;
	}

	// The Ember popup's create-from-predefined: the plan check, then the space's own metric for the key on the
	// connection (`resolveGenericMetricWithSpecific`, which creates a template's metric). `true` when it answered
	// another key: a template's metric was created (or one created earlier was found).
	private async resolvePredefined(
		key: string,
		spaceAccessId: number,
		pick: (metricSetupData: DSSetup.MetricSetupData) => void,
	): Promise<boolean> {
		await this.mmMetricsService.requestMetrics();

		const predefined = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(key, null);

		if (predefined?.handleQbOrScriptWarning()) {
			return false;
		}

		const resolved = await this.mmMetricsService.resolveGenericMetricWithSpecific(
			key,
			spaceAccessId,
		);

		pick(resolved);

		if (predefined?.isTypeCustomQueryOrGeneric) {
			this.trackMetricCreated(TrackingEvent.QUERY_CREATED, METRIC_SELECT_ORIGIN);
		} else if (predefined?.isTypeMetricCalculationOrGeneric) {
			this.trackMetricCreated(TrackingEvent.CALCULATION_CREATED, METRIC_SELECT_ORIGIN);
		}

		return resolved.key !== key;
	}

	// Resolves once the builder closed, with the metric it saved (the host then picks it), or `null`.
	private async createCustomMetricForMetricSelect(): Promise<MetricSelectSelection | null> {
		// The builder saves before it closes; the last save wins.
		const saved: { isSaved: boolean; metricData: DSSetup.MetricData | null } = {
			isSaved: false,
			metricData: null,
		};

		await this.whenClosed(
			this.mmMetricsService.openCreateNewCustomMetricPopup('metric_select', (model) => {
				saved.isSaved = true;
				saved.metricData = this.toCreatedMetricData(model);
			}),
		);

		const { isSaved, metricData } = saved;

		if (isSaved) {
			this.notifyChange();
		}

		return metricData?.spaceAccessId
			? { key: metricData.key, spaceAccessId: metricData.spaceAccessId }
			: null;
	}

	// The saved custom metric's setup, tracked as the Ember popup tracked it.
	private toCreatedMetricData(model: CustomMetric): DSSetup.MetricData | null {
		if (model instanceof CustomQuery) {
			this.trackMetricCreated(
				TrackingEvent.QUERY_CREATED,
				METRIC_SELECT_ORIGIN,
				model.metricSetup.sourceType?.key,
			);
			return model.metricSetup.metricData ?? null;
		}

		if (model instanceof Script) {
			const [metric] = this.mmMetricsService.getMetricsForScriptId(model.numericIdForSure);
			this.trackMetricCreated(
				TrackingEvent.CALCULATION_CREATED,
				METRIC_SELECT_ORIGIN,
				metric?.sourceType.key,
			);
			return metric?.metricData ?? null;
		}

		// A horizon metric.
		this.trackMetricCreated(
			TrackingEvent.QUERY_CREATED,
			METRIC_SELECT_ORIGIN,
			model.sourceObj?.sourceType.key,
		);
		return model.metricSetup.metricData ?? null;
	}

	private trackMetricCreated(event: TrackingEvent, origin: string, sourceTypeKey?: string): void {
		const data: APIData.Tracking.Data = { origin };

		if (sourceTypeKey) {
			data.datasource = sourceTypeKey;
		}

		this.analyticsService.event(event, data);
	}

	// ------ Helpers ------

	// The lookups need the metrics, custom metrics and goals loaded; the /metrics route starts that.
	private async findMetricSetup(item: MetricRegistryItem): Promise<MetricSetup | null> {
		await this.mmMetricsService.prepareForMetricEditing();

		const metricSetup = new MetricSetup({ ...item.metricSetup });

		if (!metricSetup.metric) {
			this.showNotAvailable();
			return null;
		}

		return metricSetup;
	}

	// The row's model isn't loaded, or no longer exists.
	private showNotAvailable(): void {
		this.infolayerService.error('Metric is not available.');
	}

	// The custom query, calculation or horizon metric behind the row, looked up fresh each time.
	private findCustomMetric(item: MetricRegistryItem): CustomMetric | null {
		if (item.objectId === null) {
			return null;
		}

		const id = String(item.objectId);

		switch (item.objectType) {
			case MetricType.CUSTOM_QUERY:
				return this.mmCustomQueriesService.findModel(id);
			case MetricType.METRIC_CALCULATION:
				return this.mmScriptsService.findModel(id);
			case MetricType.HORIZON:
				return this.mmHorizonMetricsService.findModel(id);
			default:
				return null;
		}
	}

	private findSavedCustomMetric(model: CustomMetric): CustomMetric | null {
		const id = model.idForSure;

		if (model instanceof CustomQuery) {
			return this.mmCustomQueriesService.findModel(id);
		}

		if (model instanceof Script) {
			return this.mmScriptsService.findModel(id);
		}

		return this.mmHorizonMetricsService.findModel(id);
	}

	// Resolves once the popup closed; at once when the opener opened none (demo, billing, one already open).
	// An editor's own Duplicate opens a second editor and then closes the first, so this follows the second.
	private whenClosed(popup: Popup | null | undefined): Promise<void> {
		if (!popup || !this.popupService.popupStack.includes(popup)) {
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			popup.on(PopupEvent.CLOSED, this, () => {
				const nextEditor = this.popupService.popupStack.find(
					(other) =>
						other !== popup &&
						other.componentInnerClass === popup.componentInnerClass &&
						(other.isOpen || other.isOpening),
				);

				resolve(nextEditor ? this.whenClosed(nextEditor) : undefined);
			});
		});
	}

	// For a popup whose callback runs only on a save.
	private async isSavedOnClose(
		open: (onSaved: () => void) => Popup | null | undefined,
	): Promise<boolean> {
		let isSaved = false;

		await this.whenClosed(
			open(() => {
				isSaved = true;
			}),
		);

		return isSaved;
	}

	// The saved custom query, calculation, horizon metric and metric ids, to tell whether a flow created one.
	private getSavedRecordIds(): Set<string> {
		const services: { modelName: string; savedRecords: DSModel[] }[] = [
			this.mmCustomQueriesService,
			this.mmScriptsService,
			this.mmHorizonMetricsService,
			this.mmMetricsService,
		];

		return new Set(
			services.flatMap(({ modelName, savedRecords }) =>
				savedRecords.map((record) => `${modelName}:${record.id}`),
			),
		);
	}

	private hasCreatedRecords(savedIds: Set<string>): boolean {
		return [...this.getSavedRecordIds()].some((id) => !savedIds.has(id));
	}

	// Each saved tag's name and colour, by id.
	private getTagLooks(): Map<string, string> {
		return new Map(
			this.mmTagsService.savedRecords.map(({ id, name, color }) => [
				String(id),
				`${name}\n${color}`,
			]),
		);
	}

	// The tags context's tags modal saves renames, recolours and deletes itself. The rows carry their own copies of
	// their tags, so the page reloads when a tag that was there before the context opened changed.
	private notifyIfTagsChanged(tagLooks: Map<string, string>): void {
		const current = this.getTagLooks();

		if ([...tagLooks].some(([id, look]) => current.get(id) !== look)) {
			this.notifyChange();
		}
	}

	// Tells the Metrics page, when it's open, to reload its rows.
	private notifyChange(): void {
		this.changeListeners.forEach((listener) => listener());
	}

	private toEditResult(
		item: MetricRegistryItem,
		name: string,
		hasOtherChanges: boolean,
	): MetricEditResult {
		return { changes: name === item.name ? {} : { name }, hasOtherChanges };
	}
}

declare module '@ember/service' {
	interface Registry {
		'metric-registry': MetricRegistryService;
	}
}
